import React from "react";
import { mdiAutorenew, mdiCogOutline } from "@mdi/js";
import { cn } from "../../styles";
import { BaseElementProps, ThemeColor } from "../../props";
import { ThemeColorIcons } from "../../constants";
import {
  CircularProgress,
  CircularProgressProps,
  Icon,
  IconProps,
  Typography,
  TypographyContent,
} from "../../atoms";

export type ProgressIndicatorState = "indeterminate" | "progress" | "complete";

export interface ProgressIndicatorProps extends BaseElementProps {
  /**
   * Controls the ring and animation behaviour.
   * - `"indeterminate"` — spinning ring, spinning icon
   * - `"progress"` — determinate ring at `progress` value, spinning icon
   * - `"complete"` — ring filled to 100%, no animation
   * @default "indeterminate"
   */
  state?: ProgressIndicatorState;

  /**
   * Thematic color applied to the ring and icon.
   * For `state="complete"`, also determines the default icon via ThemeColorIcons.
   * @default ThemeColor.none
   */
  color?: ThemeColor;

  /**
   * Completion percentage (0–100). Only used when `state="progress"`.
   */
  progress?: number;

  /**
   * Disables the spinning animation on the icon.
   * @default false
   */
  passive?: boolean;

  /**
   * Main heading text displayed below the indicator ring.
   * @default {local: "Title"}
   */
  title?: TypographyContent;

  /**
   * Supporting description text displayed below the title.
   * @default {local: "Message"}
   */
  message?: TypographyContent;

  /**
   * Action buttons displayed below the message.
   */
  actions?: React.ReactNode;

  /**
   * Props passed to the Icon element.
   */
  iconProps?: IconProps;

  /**
   * Props passed to the CircularProgress element.
   */
  circularProgressProps?: Omit<CircularProgressProps, "progress" | "color">;
}

type StateConfig = {
  getDefaultIconPath: (color: ThemeColor) => string;
  spinning: boolean;
  progressValue: number | "busy";
};

const STATE_CONFIG: Record<ProgressIndicatorState, StateConfig> = {
  indeterminate: {
    getDefaultIconPath: () => mdiAutorenew,
    spinning: true,
    progressValue: "busy",
  },
  progress: {
    getDefaultIconPath: () => mdiCogOutline,
    spinning: true,
    progressValue: 0,
  },
  complete: {
    getDefaultIconPath: (color) =>
      ThemeColorIcons[color] ?? ThemeColorIcons[ThemeColor.none],
    spinning: false,
    progressValue: 100,
  },
};

/**
 * ProgressIndicator displays an animated circular progress ring with an overlaid icon.
 * `state` controls behaviour (spinning, ring fill); `color` controls all visual theming.
 * For `state="complete"`, the default icon is automatically chosen from ThemeColorIcons
 * based on the `color` prop — e.g. ThemeColor.success → check icon, ThemeColor.error → alert icon.
 * Wrap with LocalizationProvider if using `{ local: "key" }` content.
 */
export const ProgressIndicator = ({
  state = "indeterminate",
  color = ThemeColor.primary,
  progress = 0,
  passive = false,
  title = { local: "Title" },
  message = { local: "Message" },
  actions,
  iconProps,
  circularProgressProps,
  dense = false,
  className,
}: ProgressIndicatorProps) => {
  const config = STATE_CONFIG[state];
  const progressValue =
    state === "progress" ? Math.min(100, progress + 5) : config.progressValue;
  const spinning = config.spinning && !passive;
  const hasCustomIcon = !!(
    iconProps?.path ||
    iconProps?.name ||
    iconProps?.local
  );
  const resolvedIconPath = hasCustomIcon
    ? iconProps.path
    : config.getDefaultIconPath(color);

  const ringSize = dense ? 64 : 96;
  const iconSizeClass = dense ? "w-8 h-8" : "w-12 h-12";

  return (
    <div
      className={cn(
        "flex flex-col items-center text-center",
        dense ? "gap-3" : "gap-6",
        className,
      )}
    >
      <div
        className="relative inline-flex items-center justify-center flex-shrink-0"
        style={{ width: ringSize, height: ringSize }}
      >
        <CircularProgress
          size={ringSize}
          rounded
          spinDuration={2}
          thickness={28}
          {...circularProgressProps}
          progress={progressValue as number | "busy"}
          color={color}
        />
        <Icon
          path={resolvedIconPath}
          color={color}
          muted={color === ThemeColor.none}
          {...iconProps}
          className={cn(
            "absolute",
            iconSizeClass,
            spinning && "animate-spin",
            iconProps?.className,
          )}
          style={
            spinning
              ? { animationDuration: "3s", ...iconProps?.style }
              : iconProps?.style
          }
        />
      </div>

      {(title || message) && (
        <div
          className={cn(
            "flex flex-col items-center",
            dense ? "gap-1" : "gap-2",
          )}
        >
          {title && (
            <Typography.P
              className={cn("font-medium", dense ? "text-lg" : "text-xl")}
              content={title}
            />
          )}
          {message && (
            <Typography.P
              muted
              className={cn(dense ? "text-xs" : "text-sm")}
              content={message}
            />
          )}
        </div>
      )}

      {actions && <div className="flex gap-3 justify-center">{actions}</div>}
    </div>
  );
};

ProgressIndicator.displayName = "ProgressIndicator";
